import { call } from '@advanza/api'
import { Button, Icon } from '@advanza/ui'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import BaseModal, {
    BaseModalFooter,
    BaseModalHeader,
    BaseModalMainContent,
    ScrollArea,
} from '@trustoo/components/modal/BaseModal'
import clsx from 'clsx'
import { ReactNode, useEffect, useState } from 'react'

/**
 * `call()` rejects with the parsed JSON error body for non-success responses
 * and only rejects with a real Error on network failures, so pull the message
 * off the payload instead of relying on `instanceof Error`.
 */
function extractApiErrorMessage(err: unknown, fallback: string): string {
    if (err instanceof Error) {
        return err.message
    }
    if (typeof err === 'object' && err !== null) {
        const message = (err as { message?: unknown }).message
        if (typeof message === 'string' && message.length > 0) {
            return message
        }
    }
    return fallback
}

const nlInteger = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 0 })
const nlOneDecimal = new Intl.NumberFormat('nl-NL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
})

function formatEuro(value: number): string {
    const rounded = Math.round(value)
    const sign = rounded < 0 ? '-' : ''
    return `${sign}€ ${nlInteger.format(Math.abs(rounded))}`
}

interface RoiHistoryItem {
    id: number
    aov: number
    margin_pct: number
    expected_conversion_pct: number
    cost_per_lead: number
    leads_per_month: number
    subscription: number
    roi_netto: number
    created: string | null
    created_by_user_id: number | null
    created_by_user_name: string
}

interface Props {
    providerId: number
}

const RoiCalculatorHistoryModal = NiceModal.create(({ providerId }: Props) => {
    const modal = useModal()
    const [history, setHistory] = useState<RoiHistoryItem[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!providerId) {
            return
        }
        setLoading(true)
        setError(null)
        call(`office/providers/get-provider-roi-history/${providerId}`)
            .then((response) => setHistory((response?.history as RoiHistoryItem[]) || []))
            .catch((err: unknown) =>
                setError(extractApiErrorMessage(err, 'Failed to fetch ROI history')),
            )
            .finally(() => setLoading(false))
    }, [providerId])

    const onClose = () => {
        modal.resolve()
        modal.hide()
    }

    return (
        <BaseModal
            onClose={onClose}
            maxWidth="80vw"
            className="h-[80vh] w-[80vw]"
            scrollArea={ScrollArea.CONTENT}>
            <BaseModalHeader>
                <h2 className="text-display-xs font-bold">ROI history</h2>
            </BaseModalHeader>
            <BaseModalMainContent>
                {loading && (
                    <div className="flex justify-center items-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
                    </div>
                )}

                {error && (
                    <div className="bg-red-50 border border-red-100 rounded-md p-4 mb-4">
                        <div className="flex items-center">
                            <Icon name="warning" className="text-red-600 mr-2" />
                            <div className="text-red-800">{error}</div>
                        </div>
                    </div>
                )}

                {!loading && !error && (
                    <>
                        {history.length === 0 ? (
                            <div className="text-center py-8 text-grey-700">
                                No ROI history yet. Save the calculator once to create the first
                                snapshot.
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y">
                                    <thead>
                                        <tr>
                                            <Th>When</Th>
                                            <Th>Saved by</Th>
                                            <Th>Revenue / job</Th>
                                            <Th>Margin %</Th>
                                            <Th>Conversion %</Th>
                                            <Th>Cost / lead</Th>
                                            <Th>Leads / mo</Th>
                                            <Th>Subscription</Th>
                                            <Th>Net ROI</Th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y">
                                        {history.map((item) => (
                                            <tr key={item.id}>
                                                <Td>{item.created ?? 'N/A'}</Td>
                                                <Td>{item.created_by_user_name}</Td>
                                                <Td>{formatEuro(item.aov)}</Td>
                                                <Td>{nlOneDecimal.format(item.margin_pct)}%</Td>
                                                <Td>
                                                    {nlOneDecimal.format(
                                                        item.expected_conversion_pct,
                                                    )}
                                                    %
                                                </Td>
                                                <Td>{formatEuro(item.cost_per_lead)}</Td>
                                                <Td>{nlOneDecimal.format(item.leads_per_month)}</Td>
                                                <Td>{formatEuro(item.subscription)}</Td>
                                                <Td className="font-bold">
                                                    {formatEuro(item.roi_netto)}
                                                </Td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </BaseModalMainContent>
            <BaseModalFooter>
                <Button name="secondary" onClick={onClose}>
                    Close
                </Button>
            </BaseModalFooter>
        </BaseModal>
    )
})

const Th = ({ children }: { children: ReactNode }) => (
    <th className="px-4 py-2 text-left text-sm font-bold uppercase tracking-wider">{children}</th>
)

const Td = ({ children, className = '' }: { children: ReactNode; className?: string }) => (
    <td className={clsx('px-4 py-3 whitespace-nowrap text-md', className)}>{children}</td>
)

export default RoiCalculatorHistoryModal
